// Departments dropdown in the header (no React). All of its markup is server-rendered by
// Views/Header/Partials/DepartmentsTopLevel.cshtml; this only switches panels and builds the sub category
// levels, which are lazy-loaded per department from webservicesCORE1/topnav/GetSfDeptSubCategories.
// observable.js (HeaderMiddleware.Departments) loads this on first open and talks to it through
// window.DepartmentsMainComponent.

interface DeptCategory {
    SF_CATEGORY_ID: number;
    CategoryName: string;
    SubCategories?: DeptCategory[];
    // server-rendered departments whose SubCategories have not been loaded yet
    HasSubCategories?: boolean;
}

const w = window as any;
const container = document.getElementById("divDepartmentsDropdown");
const deptRoot = container && container.querySelector<HTMLElement>("[data-dept-root]");
if (deptRoot) {
    initDepartments(deptRoot);
}

function initDepartments(root: HTMLElement) {
    const sfid = root.getAttribute("data-sfid");
    const restricted = root.getAttribute("data-restricted");
    const viewAllText = root.getAttribute("data-view-all") || "";
    const backText = root.getAttribute("data-back") || "";
    const drillList = root.querySelector("[data-dept-drill-list]") as HTMLElement;
    const wishlistCount = root.querySelector("[data-dept-wishlist-count]") as HTMLElement;
    const signOut = root.querySelector("[data-dept-signout]") as HTMLElement;

    const departments: DeptCategory[] = [];
    const items = root.querySelectorAll<HTMLElement>("[data-sf-cat-id]");
    for (let i = 0; i < items.length; i++) {
        departments.push({
            SF_CATEGORY_ID: parseInt(items[i].getAttribute("data-sf-cat-id") as string, 10),
            CategoryName: (items[i].textContent || "").trim(),
            HasSubCategories: items[i].getAttribute("data-has-subcats") === "true"
        });
    }

    // departments drilled into, outermost first
    let path: DeptCategory[] = [];
    // bumped on every panel change so a slow sub category response doesn't yank the user somewhere else
    let navVersion = 0;

    function baseUrl(): string {
        return typeof w.GetBaseUrlFromDocumentMiddleware === "function" ? w.GetBaseUrlFromDocumentMiddleware() : "";
    }

    function categoryHref(sfCatId: number) {
        return "productlist/categorydispatch?CID=" + sfCatId;
    }

    function hasSubCategories(cat: DeptCategory) {
        return cat.SubCategories ? cat.SubCategories.length > 0 : !!cat.HasSubCategories;
    }

    function showPanel(name: string, focus: boolean) {
        navVersion++;
        if (name !== "drill") {
            path = [];
        }
        const panels = root.querySelectorAll<HTMLElement>("[data-dept-panel]");
        for (let i = 0; i < panels.length; i++) {
            const isShown = panels[i].getAttribute("data-dept-panel") === name;
            panels[i].hidden = !isShown;
            if (isShown && focus) {
                const first = panels[i].querySelector<HTMLElement>("a[href], button");
                if (first) {
                    first.focus();
                }
            }
        }
    }

    function listItem(text: string, href: string, className?: string, attr?: string, attrValue?: string) {
        const li = document.createElement("li");
        const a = document.createElement("a");
        a.href = href;
        a.textContent = text;
        if (className) {
            a.className = className;
        }
        if (attr) {
            (attr === "data-sf-cat-id" ? li : a).setAttribute(attr, attrValue || "");
        }
        li.appendChild(a);
        return li;
    }

    function renderDrill(focus: boolean) {
        const current = path[path.length - 1];
        drillList.textContent = "";
        drillList.appendChild(listItem(backText, "#", "mobile-nav__back", "data-dept-back"));
        drillList.appendChild(listItem(viewAllText + " " + current.CategoryName, categoryHref(current.SF_CATEGORY_ID)));
        const subCategories = current.SubCategories || [];
        for (let i = 0; i < subCategories.length; i++) {
            const sub = subCategories[i];
            drillList.appendChild(listItem(sub.CategoryName, categoryHref(sub.SF_CATEGORY_ID),
                hasSubCategories(sub) ? "mobile-nav__parent" : undefined, "data-sf-cat-id", String(sub.SF_CATEGORY_ID)));
        }
        showPanel("drill", focus);
    }

    function drillInto(cat: DeptCategory, focus: boolean) {
        if (!cat.SubCategories && cat.HasSubCategories) {
            const requestedAt = navVersion;
            fetchSubCategories(cat.SF_CATEGORY_ID).then((subCategories) => {
                const stillWanted = navVersion === requestedAt;
                if (subCategories.length > 0) {
                    cat.SubCategories = subCategories;
                    if (stillWanted) {
                        drillInto(cat, focus);
                    }
                } else if (stillWanted) {
                    // request failed: go to the department page instead of leaving the click dead
                    window.location.href = baseUrl() + categoryHref(cat.SF_CATEGORY_ID);
                }
            });
            return;
        }
        if (hasSubCategories(cat)) {
            path.push(cat);
            renderDrill(focus);
        }
    }

    function fetchSubCategories(sfCatId: number): Promise<DeptCategory[]> {
        // default cache mode: the endpoint sends Cache-Control max-age so repeat opens across pages skip the network
        return fetch(baseUrl() + "webservicesCORE1/topnav/GetSfDeptSubCategories?cid=" + sfCatId +
            "&storefrontid=" + sfid + "&isrestricted=" + restricted, { credentials: "same-origin" })
            .then((response) => response.ok ? response.json() : [])
            .then((result) => Array.isArray(result) ? result as DeptCategory[] : [])
            .catch((error) => {
                console.error(error);
                return [] as DeptCategory[];
            });
    }

    function setCustomer(customer: any) {
        signOut.hidden = !(customer && customer.LoggedIn && customer.CustomerID > 0);
        const count = customer && customer.LoggedIn ? customer.WishListCount : 0;
        wishlistCount.hidden = !(count > 0);
        wishlistCount.textContent = count > 0 ? "  (" + count + ")" : "";
    }

    root.addEventListener("click", (e) => {
        const link = (e.target as Element).closest("a, button");
        if (!link || !root.contains(link)) {
            return;
        }
        const middleware = w.$HeaderMiddleware;
        const panel = link.getAttribute("data-dept-open");
        const langSubPath = link.getAttribute("data-dept-lang");
        const sfCatId = link.parentElement && link.parentElement.getAttribute("data-sf-cat-id");

        if (panel) {
            e.preventDefault();
            showPanel(panel, true);
            if (panel === "account") {
                middleware.MyAccountMiddleware.updateLogin();
            }
        } else if (link.hasAttribute("data-dept-back")) {
            e.preventDefault();
            path.pop();
            if (path.length > 0) {
                renderDrill(true);
            } else {
                showPanel("main", true);
            }
        } else if (link.hasAttribute("data-dept-logout")) {
            e.preventDefault();
            middleware.MyAccountMiddleware.logout();
            setCustomer(null);
        } else if (langSubPath !== null) {
            e.preventDefault();
            middleware.LanguageSelector.OnChange(langSubPath);
        } else if (sfCatId && !(e.ctrlKey || e.metaKey || e.shiftKey)) {
            // departments with sub categories drill down; modified clicks still open the department page
            const level = path.length > 0 ? path[path.length - 1].SubCategories || [] : departments;
            for (let i = 0; i < level.length; i++) {
                if (level[i].SF_CATEGORY_ID === parseInt(sfCatId, 10) && hasSubCategories(level[i])) {
                    e.preventDefault();
                    drillInto(level[i], true);
                    break;
                }
            }
        }
    });

    const languages = w.$AdjSfForSfCountryLanguages_JSON;
    if (languages && languages.length > 1) {
        (root.querySelector("[data-dept-lang-selector]") as HTMLElement).hidden = false;
        (root.querySelector("[data-dept-lang-current] span") as HTMLElement).textContent = languages[0].TRANSLATED_LANGUAGE;
        const languageList = root.querySelector("[data-dept-panel=\"language\"] ul") as HTMLElement;
        for (let i = 1; i < languages.length; i++) {
            const li = listItem(languages[i].TRANSLATED_LANGUAGE, "#", undefined, "data-dept-lang", languages[i].URL_SUB_PATH);
            li.className = "menu__item";
            languageList.appendChild(li);
        }
    }

    try {
        if (w.$SfCustomerSubject !== undefined) {
            w.$SfCustomerSubject.addObserver({ id: 15, name: "", update: setCustomer });
        }
    } catch (exception) {
        console.log(exception);
    }

    w.DepartmentsMainComponent = {
        // called by observable.js each time the dropdown opens, optionally to drill straight into a department
        showDepartments_External_Notification(parentCategoryId?: number) {
            showPanel("main", false);
            for (let i = 0; i < departments.length; i++) {
                if (departments[i].SF_CATEGORY_ID === parentCategoryId) {
                    drillInto(departments[i], false);
                    break;
                }
            }
        }
    };
    w.$HeaderMiddleware.departmentsMainComponent_FinallyDidMount();
}
